import React, { useState, useRef, useEffect } from "react";
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  SoundOutlined,
  MutedOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  DownloadOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import "./SessionRecordingPlayer.scss";

let timeout: NodeJS.Timeout | undefined;
let hoveredSecond: number | null = null;

interface VideoPreviewProps {
  videoUrl: string;
}

const SessionRecordingPlayer: React.FC<VideoPreviewProps> = ({ videoUrl }) => {
  const [hasError, setHasError] = useState<boolean>(false);
  const [snapshots, setSnapshots] = useState<string>("");
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isMetadata, setIsMetaData] = useState<boolean>(false);
  const [duration, setDuration] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [volume, setVolume] = useState<number>(1);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const previousVolumeRef = useRef<number>(1);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showControls, setShowControls] = useState<boolean>(true);
  const [showSpeedMenu, setShowSpeedMenu] = useState<boolean>(false);
  const [isSnapshotLoaded, setIsSnapshotLoaded] = useState<boolean>(false);
  const animationRef = useRef<number | null>(null);
  const progressRef = useRef<HTMLInputElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const secondVideoRef = useRef<HTMLVideoElement | null>(null);
  const snapshotRef = useRef<HTMLDivElement | null>(null);
  const videoWrapperRef = useRef<HTMLDivElement | null>(null);
  const videoContainerRef = useRef<HTMLDivElement | null>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const snapshotTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingSnapshotTimeRef = useRef<number | null>(null);

  useEffect(() => {
    setHasError(false);
  }, [videoUrl]);

  useEffect(() => {
    if (videoRef.current && isMetadata && progressRef.current) {
      const seconds = videoRef.current.duration;
      if (seconds && isFinite(seconds) && seconds > 0) {
        setDuration(seconds);
        progressRef.current.max = String(seconds);
      }
      videoRef.current.volume = volume;
    }
  }, [isMetadata, volume]);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const handleTimeUpdate = () => {
      if (videoElement.currentTime && isFinite(videoElement.currentTime)) {
        setCurrentTime(videoElement.currentTime);
      }
    };

    const handleDurationChange = () => {
      if (videoElement.duration && isFinite(videoElement.duration) && videoElement.duration > 0) {
        setDuration(videoElement.duration);
        if (progressRef.current) {
          progressRef.current.max = String(videoElement.duration);
        }
      }
    };

    videoElement.addEventListener("timeupdate", handleTimeUpdate);
    videoElement.addEventListener("durationchange", handleDurationChange);

    return () => {
      videoElement.removeEventListener("timeupdate", handleTimeUpdate);
      videoElement.removeEventListener("durationchange", handleDurationChange);
    };
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      try {
        const fullscreenElement =
          document.fullscreenElement ||
          (document as any).webkitFullscreenElement ||
          (document as any).mozFullScreenElement ||
          (document as any).msFullscreenElement;

        const isOurFullscreen =
          fullscreenElement === videoContainerRef.current ||
          fullscreenElement === videoRef.current;
        setIsFullscreen(!!isOurFullscreen);
      } catch (error) {}
    };

    const options: AddEventListenerOptions = { passive: true };

    if (document.addEventListener) {
      document.addEventListener(
        "fullscreenchange",
        handleFullscreenChange,
        options
      );
      document.addEventListener(
        "webkitfullscreenchange" as any,
        handleFullscreenChange,
        options
      );
      document.addEventListener(
        "mozfullscreenchange" as any,
        handleFullscreenChange,
        options
      );
      document.addEventListener(
        "MSFullscreenChange" as any,
        handleFullscreenChange,
        options
      );
    }

    return () => {
      // Clean up event listeners
      if (document.removeEventListener) {
        document.removeEventListener(
          "fullscreenchange",
          handleFullscreenChange,
          options
        );
        document.removeEventListener(
          "webkitfullscreenchange" as any,
          handleFullscreenChange,
          options
        );
        document.removeEventListener(
          "mozfullscreenchange" as any,
          handleFullscreenChange,
          options
        );
        document.removeEventListener(
          "MSFullscreenChange" as any,
          handleFullscreenChange,
          options
        );
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showSpeedMenu && !(event.target as Element).closest(".speedControl")) {
        setShowSpeedMenu(false);
      }
    };

    if (showSpeedMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showSpeedMenu]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  // Handle snapshot capture when video seeks
  useEffect(() => {
    const videoElement = secondVideoRef.current;
    if (!videoElement) return;

    const handleSeeked = () => {
      if (pendingSnapshotTimeRef.current !== null) {
        shoot(videoElement);
        pendingSnapshotTimeRef.current = null;
      }
    };

    videoElement.addEventListener("seeked", handleSeeked);
    return () => {
      videoElement.removeEventListener("seeked", handleSeeked);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkMetadata = () => {
    setIsMetaData(true);
    if (videoRef.current) {
      const seconds = videoRef.current.duration;
      if (seconds && isFinite(seconds) && seconds > 0) {
        setDuration(seconds);
        if (progressRef.current) {
          progressRef.current.max = String(seconds);
        }
      }
    }
  };

  const changePlayerCurrentTime = () => {
    if (progressRef.current) {
      const value = parseFloat(progressRef.current.value) || 0;
      progressRef.current.style.setProperty(
        "--seek-before-width",
        `${(value / duration) * 100}%`
      );
    }
  };

  const whilePlaying = () => {
    if (progressRef.current !== null && videoRef.current !== null) {
      const time = videoRef.current.currentTime;
      if (time && isFinite(time)) {
        setCurrentTime(time);
        progressRef.current.value = String(time);
        changePlayerCurrentTime();
      }
      animationRef.current = requestAnimationFrame(whilePlaying);
    }
  };

  const onPlaying = () => {
    setIsPlaying(true);
    whilePlaying();
  };

  const onPause = () => {
    setIsPlaying(false);
    if (timeout) {
      clearTimeout(timeout);
    }
    if (animationRef.current !== null) {
      cancelAnimationFrame(animationRef.current);
    }
  };

  const changeRange = () => {
    if (videoRef.current && progressRef.current) {
      const newTime = parseFloat(progressRef.current.value) || 0;
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
      changePlayerCurrentTime();
    }
  };

  const onSliderHover = (e: React.MouseEvent<HTMLInputElement>) => {
    // Clear any pending snapshot timeout
    if (snapshotTimeoutRef.current) {
      clearTimeout(snapshotTimeoutRef.current);
    }

    const target = e.target as HTMLInputElement;
    const duration = videoRef.current?.duration || 0;
    let hoverTime = (
      ((e.clientX - target.offsetLeft) / target.clientWidth) * duration
    );

    if (hoverTime < 0) {
      hoverTime = 0;
    }

    hoveredSecond = hoverTime;

    // Update snapshot position relative to progress bar
    if (snapshotRef.current && progressRef.current) {
      const progressBar = progressRef.current;
      const snapshotWidth = 150; // Width of snapshot container
      const progressRect = progressBar.getBoundingClientRect();
      const containerRect = progressBar.parentElement?.getBoundingClientRect();

      if (progressRect && containerRect) {
        // Calculate position relative to the progress bar
        const relativeX = e.clientX - progressRect.left;
        // Center the snapshot on the cursor (half of snapshot width)
        let leftPosition = relativeX - snapshotWidth / 2;

        // Keep snapshot within bounds of the progress bar
        const minLeft = 0;
        const maxLeft = progressRect.width - snapshotWidth;
        leftPosition = Math.max(minLeft, Math.min(maxLeft, leftPosition));

        // Position relative to the progress bar's container
        const containerLeft = progressRect.left - containerRect.left;
        snapshotRef.current.style.left = containerLeft + leftPosition + "px";
      }
    }

    // Debounce snapshot capture to reduce flickering
    snapshotTimeoutRef.current = setTimeout(() => {
      if (secondVideoRef.current && videoRef.current) {
        const targetTime = hoverTime;

        // Only seek if the time has changed significantly (more than 0.1 seconds)
        if (Math.abs(secondVideoRef.current.currentTime - targetTime) > 0.1) {
          pendingSnapshotTimeRef.current = targetTime;
          secondVideoRef.current.currentTime = targetTime;
        } else {
          // If already at the right time, capture immediately
          shoot(secondVideoRef.current);
        }
      }
    }, 50); // Small delay to debounce rapid mouse movements
  };

  const shoot = (video: HTMLVideoElement) => {
    // Check if video is ready and has valid dimensions
    if (
      !video ||
      video.readyState < 2 ||
      video.videoWidth === 0 ||
      video.videoHeight === 0
    ) {
      return;
    }

    try {
      let canvas = capture(video);
      if (canvas) {
        const dataUrl = canvas.toDataURL();
        setIsSnapshotLoaded(false); // Reset loaded state when new snapshot is captured
        setSnapshots(dataUrl);
      }
    } catch (error) {
      console.error("Error capturing snapshot:", error);
    }
  };

  const capture = (video: HTMLVideoElement): HTMLCanvasElement | null => {
    if (!video || video.videoWidth === 0 || video.videoHeight === 0) {
      return null;
    }

    let w = video.videoWidth;
    let h = video.videoHeight;
    let canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    let ctx = canvas.getContext("2d");

    // Ensure we have a valid context
    if (!ctx) {
      return null;
    }

    ctx.drawImage(video, 0, 0, w, h);
    return canvas;
  };

  const onOutsideSlider = () => {
    // Clear any pending snapshots
    if (snapshotTimeoutRef.current) {
      clearTimeout(snapshotTimeoutRef.current);
    }
    pendingSnapshotTimeRef.current = null;
    setSnapshots("");
    setIsSnapshotLoaded(false);
  };

  const handleSnapshotImageLoad = () => {
    setIsSnapshotLoaded(true);
  };

  const handleSnapshotImageError = () => {
    setIsSnapshotLoaded(false);
  };

  const calculateTime = (secs: number | null | undefined): string => {
    if (secs === null || secs === undefined || isNaN(secs)) {
      return "0:00";
    }
    const hours = Math.floor(secs / 3600);
    const returnedHours = hours < 10 ? `0${hours}` : `${hours}`;
    const minutes = Math.floor((secs / 60) % 60);
    const returnedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
    const seconds = Math.floor(secs % 60);
    const returnedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;

    if (hours >= 1) {
      return `${returnedHours}:${returnedMinutes}:${returnedSeconds}`;
    } else if (minutes >= 1) {
      return `${returnedMinutes}:${returnedSeconds}`;
    } else {
      return `0:${returnedSeconds}`;
    }
  };

  const onVideoHover = () => {
    // This function is kept for compatibility but visibility is now handled by showControls state
  };

  const onMouseLeaveVideo = () => {
    // This function is kept for compatibility but visibility is now handled by showControls state
  };

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      setIsMuted(newVolume === 0);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      if (isMuted) {
        const restoreVolume = previousVolumeRef.current || 0.5;
        videoRef.current.volume = restoreVolume;
        setVolume(restoreVolume);
        setIsMuted(false);
      } else {
        previousVolumeRef.current = volume;
        videoRef.current.volume = 0;
        setIsMuted(true);
      }
    }
  };

  const handleSpeedChange = (speed: number) => {
    setPlaybackSpeed(speed);
    setShowSpeedMenu(false);
  };

  const toggleFullscreen = async () => {
    // Try container first, then fallback to video element
    const containerElement = videoContainerRef.current;
    const videoElement = videoRef.current;
    const element = containerElement || videoElement;

    if (!element) {
      console.warn("No element available for fullscreen");
      return;
    }

    try {
      // Check if currently in fullscreen
      const doc = document as any;
      const fullscreenElement =
        document.fullscreenElement ||
        doc.webkitFullscreenElement ||
        doc.mozFullScreenElement ||
        doc.msFullscreenElement;

      const isFullscreen = !!fullscreenElement;
      const isOurElement =
        fullscreenElement === containerElement ||
        fullscreenElement === videoElement;

      if (!isFullscreen || !isOurElement) {
        // Enter fullscreen
        const el = element as any;
        if (element.requestFullscreen) {
          // Standard API - preferred
          await element.requestFullscreen();
        } else if (el.webkitRequestFullscreen) {
          // Safari
          el.webkitRequestFullscreen();
        } else if (el.mozRequestFullScreen) {
          // Firefox
          el.mozRequestFullScreen();
        } else if (el.msRequestFullscreen) {
          // IE/Edge
          el.msRequestFullscreen();
        } else {
          console.warn("Fullscreen API not supported in this browser");
        }
      } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
          // Standard API
          await document.exitFullscreen();
        } else if (doc.webkitExitFullscreen) {
          // Safari
          doc.webkitExitFullscreen();
        } else if (doc.mozCancelFullScreen) {
          // Firefox
          doc.mozCancelFullScreen();
        } else if (doc.msExitFullscreen) {
          // IE/Edge
          doc.msExitFullscreen();
        }
      }
    } catch (error) {
      // Handle errors gracefully
      const err = error as Error;
      if (err.name === "NotAllowedError") {
        console.warn("Fullscreen request denied by browser or user");
      } else if (err.name === "TypeError") {
        // Silently ignore TypeError from parent app conflicts
      } else {
        console.error("Fullscreen operation failed:", error);
      }
    }
  };

  const handleDownload = async () => {
    if (!videoRef.current?.src) return;

    try {
      // Fetch the video as a blob to handle cross-origin issues
      const response = await fetch(videoRef.current.src);
      const blob = await response.blob();

      // Create a blob URL
      const blobUrl = window.URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `video-${Date.now()}.webm`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading video:", error);
      // Fallback: try direct download (may not work for CORS)
      const link = document.createElement("a");
      link.href = videoRef.current.src;
      link.download = `video-${Date.now()}.webm`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const resetControlsTimeout = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  };

  const handleVideoMouseMove = () => {
    onVideoHover();
    resetControlsTimeout();
  };

  const handleVideoMouseLeave = () => {
    onMouseLeaveVideo();
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    if (isPlaying) {
      setShowControls(false);
    }
  };

  const hasValidUrl = Boolean(videoUrl && videoUrl.trim() !== "");

  if (!hasValidUrl) {
    return (
      <div className="wrapper">
        <div className="videoContainer" ref={videoContainerRef}>
          <div className="videoWrapper" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 200, background: "var(--ant-color-fill-quaternary)", borderRadius: 8 }}>
            <span style={{ color: "var(--ant-color-text-secondary)" }}>No recording available</span>
          </div>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="wrapper">
        <div className="videoContainer" ref={videoContainerRef}>
          <div className="videoWrapper" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 200, background: "var(--ant-color-fill-quaternary)", borderRadius: 8 }}>
            <span style={{ color: "var(--ant-color-text-secondary)" }}>This recording cannot be played. The format may not be supported or the source is unavailable.</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wrapper">
      <div className="videoContainer" ref={videoContainerRef}>
        <div
          ref={videoWrapperRef}
          className="videoWrapper"
          onMouseEnter={() => {
            setIsHovered(true);
            resetControlsTimeout();
          }}
          onMouseLeave={() => {
            setIsHovered(false);
            handleVideoMouseLeave();
          }}
          onMouseMove={handleVideoMouseMove}
        >
          <video
            src={videoUrl}
            ref={videoRef}
            preload="auto"
            crossOrigin="anonymous"
            onLoadedMetadata={checkMetadata}
            onPlaying={onPlaying}
            onPause={onPause}
            onError={() => setHasError(true)}
          ></video>
          <button
            className={`playPauseButton ${
              isPlaying && !isHovered ? "hidden" : ""
            }`}
            onClick={togglePlayPause}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {isPlaying ? (
              <PauseCircleOutlined className="playPauseIcon" />
            ) : (
              <PlayCircleOutlined className="playPauseIcon" />
            )}
          </button>

          {/* Custom Controls Bar */}
          <div
            className={`customControls ${
              showControls || !isPlaying ? "visible" : "hidden"
            }`}
          >
            <div className="controlsRow">
              <div className="controlsLeft">
                <button className="controlButton" onClick={togglePlayPause}>
                  {isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                </button>

                <div className="volumeControl">
                  <button className="controlButton" onClick={toggleMute}>
                    {isMuted || volume === 0 ? (
                      <MutedOutlined />
                    ) : (
                      <SoundOutlined />
                    )}
                  </button>
                  <input
                    type="range"
                    className="volumeSlider"
                    min="0"
                    max="1"
                    step="0.01"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                  />
                </div>

                <div className="timeDisplay">
                  <span>
                    {calculateTime(currentTime)}
                  </span>
                  <span> / </span>
                  <span>{calculateTime(duration)}</span>
                </div>
              </div>

              <div className="controlsRight">
                <div className="speedControl">
                  <button
                    className="controlButton"
                    onClick={() => setShowSpeedMenu(!showSpeedMenu)}
                  >
                    <SettingOutlined />
                    <span className="speedText">{playbackSpeed}x</span>
                  </button>
                  {showSpeedMenu && (
                    <div className="speedMenu">
                      {[0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map((speed) => (
                        <button
                          key={speed}
                          className={`speedOption ${
                            playbackSpeed === speed ? "active" : ""
                          }`}
                          onClick={() => handleSpeedChange(speed)}
                        >
                          {speed}x
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <button className="controlButton" onClick={handleDownload}>
                  <DownloadOutlined />
                </button>

                <button className="controlButton" onClick={toggleFullscreen}>
                  {isFullscreen ? (
                    <FullscreenExitOutlined />
                  ) : (
                    <FullscreenOutlined />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        <input
          type="range"
          className={`amount-progress ${
            showControls || !isPlaying || isHovered ? "visible" : "hidden"
          }`}
          step="any"
          ref={progressRef}
          min={0}
          onMouseMove={(e) => {
            setIsHovered(true);
            resetControlsTimeout();
            onSliderHover(e);
          }}
          onMouseOut={() => {
            onOutsideSlider();
          }}
          onChange={changeRange}
          value={currentTime}
        />

        <div
          ref={snapshotRef}
          className={`snapshotContainer ${
            isSnapshotLoaded && snapshots ? "visible" : "hidden"
          }`}
        >
          {snapshots && (
            <img
              src={snapshots}
              className="snapshotImg"
              alt="snapshot"
              onLoad={handleSnapshotImageLoad}
              onError={handleSnapshotImageError}
            />
          )}
          <div className="snapshotTime">{calculateTime(hoveredSecond)}</div>
        </div>
      </div>
      <video
        src={videoUrl}
        ref={secondVideoRef}
        className="secondVideoElement"
        controls
        preload="auto"
        crossOrigin="anonymous"
        onLoadedMetadata={checkMetadata}
        onPlaying={onPlaying}
        onPause={onPause}
      ></video>
    </div>
  );
};

export default SessionRecordingPlayer;
